import { useCallback, useState } from 'react';

/**
 * A hook that manages the anchor element for the {@link Dropdown} component in state, using a ref
 * callback that is attached to the underlying target element whose click action triggers the
 * opening/closing of the {@link Dropdown}.
 *
 * This hook helps avoid the following error from Material UI:
 *
 * >>> The anchorEl prop provided to the component is invalid.
 *
 * This error occurs when there is a race condition where the {@link Popover} is rendered before
 * the target element is created, leading to a situation where the anchor element is null when the
 * {@link Popover} is first rendered.
 *
 * By using a ref callback instead of a {@link MutableRefObject}, the anchor element is stored in
 * state as soon as React attaches the DOM node, triggering a re-render that allows the
 * {@link Popover} to render with a valid anchor element.
 */
export const useDropdownAnchorElement = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const ref = useCallback((node: HTMLButtonElement | null) => {
    setAnchorEl(node);
  }, []);

  return [ref, anchorEl] as const;
};
